import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Copy, Ban, RefreshCw, Search } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const eur = (v) => (v === null || v === undefined) ? '—' : Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

export const PartnerLinksTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback((q = '') => {
    setLoading(true);
    api.get('/partner-links', { params: q ? { search: q } : {} })
      .then(res => setData(res.data))
      .catch(err => toast.error(err.response?.data?.detail || 'Errore caricamento link'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = (url) => { navigator.clipboard.writeText(url); toast.success('Link copiato negli appunti'); };
  const revoke = async (l) => {
    if (!window.confirm(`Revocare il link di ${l.name || l.code}? Non potrà più creare preventivi.`)) return;
    try { const r = await api.post(`/partner-links/${l.id}/revoke`); toast.success(r.data.message); load(search); }
    catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const regenerate = async (l) => {
    if (!window.confirm(`Generare un nuovo link per ${l.name || l.code}? Il vecchio smetterà di funzionare.`)) return;
    try { const r = await api.post(`/partner-links/${l.id}/regenerate`); toast.success(r.data.message); load(search); }
    catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  return (
    <div className="space-y-3" data-testid="partner-links-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-700">Link Preventivi Partner & Segnalatori</p>
          <p className="text-xs text-slate-500">Ogni partner ha un link personale per creare preventivi (compenso 10% sull'incassato della locazione). I segnalatori HW Academy ricevono il link automaticamente dal loro pannello.</p>
        </div>
        <form onSubmit={e => { e.preventDefault(); load(search); }} className="flex items-center gap-1.5">
          <input className="px-3 py-1.5 border rounded-lg text-sm" placeholder="Cerca nome, codice, email..." value={search} onChange={e => setSearch(e.target.value)} data-testid="partner-links-search" />
          <button type="submit" className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600" data-testid="partner-links-search-btn"><Search className="h-4 w-4" /></button>
        </form>
      </div>
      {loading ? <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div> : (
        <>
          <p className="text-xs text-slate-400">{data?.links?.length || 0} mostrati su {data?.total || 0}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-xs text-slate-500">
                <th className="text-left py-2">Partner</th><th className="text-left py-2">Tipo</th>
                <th className="text-left py-2">Attività</th><th className="text-left py-2">Compensi</th>
                <th className="text-left py-2">Stato</th><th className="text-left py-2">Azioni</th>
              </tr></thead>
              <tbody>
                {(data?.links || []).map(l => (
                  <tr key={l.id} className="border-b last:border-0" data-testid={`partner-link-row-${l.code}`}>
                    <td className="py-2 pr-2">
                      <p className="font-medium">{l.name || '—'} <span className="text-xs text-slate-400">({l.code})</span></p>
                      <p className="text-xs text-slate-400">{l.email || 'nessuna email'}</p>
                    </td>
                    <td className="py-2 pr-2"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${l.kind === 'segnalatore' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>{l.kind === 'segnalatore' ? 'Segnalatore HWA' : 'Partner'}</span></td>
                    <td className="py-2 pr-2 text-xs">{l.activity ? <>{l.activity.quotes} preventivi · <b className="text-emerald-700">{l.activity.booked} prenotati</b></> : '—'}</td>
                    <td className="py-2 pr-2 text-xs font-semibold text-emerald-700">{l.commissions ? eur(l.commissions.total) : '—'}</td>
                    <td className="py-2 pr-2">{l.active ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Attivo</span> : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600">Revocato</span>}</td>
                    <td className="py-2">
                      <div className="flex items-center gap-1">
                        {l.active && <button onClick={() => copy(l.url)} title="Copia link" className="p-1 rounded hover:bg-blue-50 text-blue-600" data-testid={`partner-link-copy-${l.code}`}><Copy className="h-4 w-4" /></button>}
                        {l.active && <button onClick={() => revoke(l)} title="Revoca link" className="p-1 rounded hover:bg-red-50 text-red-500" data-testid={`partner-link-revoke-${l.code}`}><Ban className="h-4 w-4" /></button>}
                        <button onClick={() => regenerate(l)} title="Genera nuovo link" className="p-1 rounded hover:bg-slate-100 text-slate-500" data-testid={`partner-link-regen-${l.code}`}><RefreshCw className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default PartnerLinksTab;
